import { useCallback } from 'react';
import { AUTH_BYPASS, acquireApiToken } from '../config/auth';
import { API_BASE_URL } from '../config/api';

/**
 * Hook returning a `fetch`-like function that automatically attaches an
 * MSAL access token to outgoing requests against the app-commercial API.
 *
 * Usage:
 *   const apiFetch = useApi();
 *   const data = await apiFetch('/sell-dates').then(r => r.json());
 *
 * Phase 13: pick the Majans-tenant account deterministically. Previously
 * we used `accounts[0]`, which can pick the wrong account when the user
 * has multiple Microsoft accounts cached. Bug pattern was: data appears
 * missing in normal browser but present in incognito, because incognito
 * starts with one account only. Also surface silent-token-failure: if
 * the refresh token expired and renewal fails, we now trigger an
 * interactive redirect instead of letting the request go through
 * unauthenticated (which would silently fall back to defaults).
 */
export function useApi() {
  return useCallback(
    async (path: string, init: RequestInit = {}): Promise<Response> => {
      const url = path.startsWith('http') ? path : `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;

      const headers = new Headers(init.headers);
      if (!headers.has('Content-Type') && init.body) {
        headers.set('Content-Type', 'application/json');
      }

      if (!AUTH_BYPASS) {
        // acquireApiToken picks the active (Majans-tenant) account, tries silent
        // acquisition, and falls back to an interactive redirect when silent
        // fails (e.g. the `timed_out` iframe renewal when third-party cookies
        // are blocked) — so authed pages recover instead of dead-ending.
        const token = await acquireApiToken();
        headers.set('Authorization', `Bearer ${token}`);
      }

      return fetch(url, { ...init, headers });
    },
    [],
  );
}
